import React, { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { info, error as logAppInsightError } from '../../utils/logger';
import { logError } from "../../utils/logError";
import Loading from "../layout/Loading";
import { getAccessToken } from "../../services/authApi";
import { getUserDetails, getTpoSettingsAllBasicInfo } from "../../services/userApi";
import '../../common.css';
import { getOrgConfig } from "../../utils/organizationConfigUtil";
const API_DOMAIN = process.env.REACT_APP_TPOC_API_DOMAIN;

// Business lines in display order. Adding a business line = add one entry here:
// both the mapping from the Encompass channel value and the dropdown options are
// derived from this table.
const BUSINESS_LINES = [
    { value: "Wholesale", channelKeywords: ["broker", "wholesale"] },
    { value: "Correspondent", channelKeywords: ["correspondent"] }
];

const ALL_BUSINESS_LINES = BUSINESS_LINES.map(({ value }) => value);

const mapChannelTypeToBusinessLine = (channelValue) => {
    const normalizedValue = `${channelValue ?? ""}`.toLowerCase();
    const match = BUSINESS_LINES.find(({ channelKeywords }) =>
        channelKeywords.some((keyword) => normalizedValue.includes(keyword))
    );
    return match ? match.value : "";
};

const ContactForm = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const isPopup = searchParams.get("popup") === "true";
    console.log("POPUP value", isPopup);

    const [form, setForm] = useState({
        authCode: '',
        name: '',
        phoneNumber: '',
        email: '',
        loanNumber: '',
        message: '',
        channel: '',
    });
    const [submitted, setSubmitted] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [errors, setErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [loanNumberUI, setLoanNumberUI] = useState(null);
    const [loanChannel, setLoanChannel] = useState(null);
    const [isLoading, setIsLoading] = useState(!isPopup); // Only show loading for non-popup initially
    const [channelTypes, setChannelTypes] = useState([]);
    const helpText = getOrgConfig().contactSupportHelpText || "How can we help you? Send us a message.";

    // Memoised so the preselect effect below does not re-run on every render.
    const popupChannelOptions = useMemo(() => Array.from(new Set(
        (Array.isArray(channelTypes) ? channelTypes : [channelTypes])
            .map(mapChannelTypeToBusinessLine)
            .filter(Boolean)
    )), [channelTypes]);
    const isChannelDisabled = !isPopup || popupChannelOptions.length <= 1;

    const handleClose = () => {
        if (isPopup) {
            navigate(-1); // Go back in router history
            // OR navigate('/'); // Go to specific route
        } else {
            window.history.back();
        }
    };
    useEffect(() => {
        if (window.elli && window.elli.script && window.elli.script.guest) {
            window.elli.script.guest.create();
            // Only initialize loan object for non-popup mode
            if (!isPopup) {
                initializeAuthObject();
            }
        } else {
            console.warn("elli.script.guest is not available.");
        }
    }, []);

    useEffect(() => {
        // For popup, we can prefill name and email immediately
        if (isPopup) {
            fetchUserDetails();
        }
        // For non-popup, we'll wait for loanNumber to be set
    }, [isPopup]);

    useEffect(() => {
        // When loanNumber changes and it's not a popup, fetch user details
        if (!isPopup && loanNumberUI) {
            fetchUserDetails();
        }
    }, [loanNumberUI, isPopup]);

    useEffect(() => {
        // Auto-populate business line only for non-popup flow
        if (!isPopup && loanChannel) {
            const mappedChannel = mapChannelTypeToBusinessLine(loanChannel);
            setForm((prev) => ({
                ...prev,
                channel: mappedChannel || prev.channel
            }));
        }
    }, [loanChannel, isPopup]);

    useEffect(() => {
        // For popup flow, if organization has exactly one channel type, preselect and lock it.
        if (isPopup && popupChannelOptions.length === 1) {
            setForm((prev) => ({
                ...prev,
                channel: popupChannelOptions[0]
            }));
        }
    }, [isPopup, popupChannelOptions]);

    const fetchUserDetails = async () => {
        setIsLoading(true);
        try {
            const tpoAuthObject = await window.elli.script.getObject("auth");
            const user = await tpoAuthObject.getUser();
            info("ExternalUserDetails loaded", { component: "Contact Support", userid: user.id });
            const authCode = await tpoAuthObject.createAuthCode();
            const token = await getAccessToken(authCode);

            if (user && token) {
                const userData = await getUserDetails(token, user.id);
                const orgId = userData?.organization?.entityId;

                if (orgId) {
                    try {
                        const tpoSettings = await getTpoSettingsAllBasicInfo(token, orgId);
                        const channels = tpoSettings?.companyDetails?.channels ?? [];
                        setChannelTypes(Array.isArray(channels) ? channels : [channels]);
                    } catch (channelError) {
                        console.error("TPO basicInfo channel check failed:", channelError);
                        setChannelTypes(["Broker"]);
                    }
                } else {
                    setChannelTypes(["Broker"]);
                }

                setForm((prev) => ({
                    ...prev,
                    name: `${userData?.firstName ?? ""} ${userData?.lastName ?? ""}`,
                    phoneNumber: userData?.cellPhone ?? '',
                    email: userData?.email ?? '',
                    loanNumber: (!isPopup && loanNumberUI) ? loanNumberUI : ''
                }));
                setIsLoading(false);
            }
        } catch (error) {
            const errorCode = logError("USER_FETCH_ERROR");
            logAppInsightError(`${errorCode} - fetching external user details.`, {
                problemId: errorCode,
                errorCode,
                rawError: error,
                errorMessage: `${errorCode} - fetching external user details.`,
                component: "ContactForm"
            });
            setIsLoading(false);
        }
        finally {
            setIsLoading(false);
        }
    };

    const initializeAuthObject = async () => {
        try {
            window.elli.script.getObject("loan").then(async function (loanObject) {
                loanObject.all().then(async function (loan) {
                    console.log("Loan details in contact support", loan);
                    setLoanNumberUI(loan.loanNumber);
                    setLoanChannel(loan.channel);
                    info("Contat support page loaded", { loanid: loan.id || "N/A" });
                });
            });
        } catch (error) {
            console.error("Auth block Error:", error);
            if (isPopup) {
                setIsLoading(false);
            }
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm((prev) => ({ ...prev, [name]: value }));
        setErrors((prev) => ({ ...prev, [name]: '' }));
    };

    const validate = () => {
        const newErrors = {};
        if (!form.name.trim()) newErrors.name = "Name is required";
        // if (!form.phoneNumber.trim()) newErrors.phoneNumber = "Phone number is required";
        if (!form.email.trim()) newErrors.email = "Email is required";
        if (!form.message.trim()) newErrors.message = "Message is required";
        if (!form.channel) newErrors.channel = "Please select a business line";
        return newErrors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        const validationErrors = validate();
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            return;
        }
        setSubmitting(true);
        const tpoAuthObject = await window.elli.script.getObject("auth");
        tpoAuthObject.getUser().then(async function (user) {
            const authCode = await tpoAuthObject.createAuthCode();
            const url = `${API_DOMAIN}/api/support/contactsupportemail`;
            try {
                const formDataWithAuth = {
                    ...form,
                    authCode: authCode,
                    channel: form.channel
                };
                const response = await fetch(url, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(formDataWithAuth),
                });

                if (!response.ok) throw new Error("Failed to submit form");
                setSuccessMessage("Email sent successfully!");
                setTimeout(() => {
                    setSuccessMessage("");
                }, 3000);
                if (isPopup) {
                    setTimeout(handleClose, 1500); // Close after success message shows
                }
                setSubmitted(true);
            } catch (err) {
                setErrorMessage(err.message);
                console.error(err);
                setTimeout(() => {
                    setErrorMessage("");
                }, 3000);
            } finally {
                setSubmitting(false);
            }
        });
    };

    if (isLoading) {
        return <Loading />;
    }

    return (
        <>
            {successMessage && (
                <div className="fixed top-6 right-6 z-50 flex items-start gap-3 bg-green-100 border-l-4 border-green-600 text-green-800 p-4 rounded-md shadow-lg max-w-sm animate-fade-in">
                    <div className="text-2xl">✅</div>
                    <div className="flex-1">
                        <div className="font-semibold mb-1">Success</div>
                        <div className="text-sm leading-relaxed break-words">{successMessage}</div>
                    </div>
                    <button
                        onClick={() => setSuccessMessage('')}
                        className="ml-4 text-green-500 hover:text-green-700 focus:outline-none transition-colors duration-200"
                        aria-label="Close"
                    >
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                    </button>
                </div>
            )}

            {errorMessage && (
                <div className="fixed top-6 right-6 z-50 flex items-start gap-3 bg-red-100 border-l-4 border-red-600 text-red-800 p-4 rounded-md shadow-lg max-w-sm animate-fade-in">
                    <div className="text-2xl">❌</div>
                    <div className="flex-1">
                        <div className="font-semibold mb-1">Error</div>
                        <div className="text-sm leading-relaxed break-words">{errorMessage}</div>
                    </div>
                    <button
                        onClick={() => setErrorMessage('')}
                        className="ml-4 text-red-500 hover:text-red-700 focus:outline-none transition-colors duration-200"
                        aria-label="Close"
                    >
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                    </button>
                </div>
            )}
            <div className="min-h-screen p-6 summary-font">
                {/* Full-width header */}
                <div className="panel-heading font-bold text-lg md:text-xl mb-4">
                    Contact Support
                </div>
                {/* Left-aligned form container */}
                <div className="w-3/5 ml-6 p-2">
                    {submitted ? (
                        <div className="border border-[var(--blue-2)] rounded bg-[#f9f9f9]">
                            <div className="section-header">Thanks for contacting us!</div>
                            <div className="bg-white rounded-lg shadow-xl w-full max-w-8xl p-4 overflow-auto">
                                <div className="flex items-start">
                                    <span className="text-green-500 mr-2 mt-0.5">✔</span>
                                    <p className="form-label mb-1">Our team will get back to you shortly.</p>
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div className="w-full">
                            <div>
                                <h2 className="text-sm md:text-base font-semibold text-gray-700 mb-4">
                                    {helpText}
                                </h2>

                                <form className="space-y-4" onSubmit={handleSubmit}>
                                    {/* Two-column layout */}
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        {/* Left column */}
                                        <div>
                                            <label className="form-label mb-1 w-50">
                                                <span className="text-red-500">*</span> Name:
                                            </label>
                                            <input
                                                name="name"
                                                value={form.name}
                                                onChange={handleChange}
                                                className="w-full border rounded px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-brand-primary"
                                            />
                                            {errors.name && <p className="text-xs text-red-500">{errors.name}</p>}
                                            {/* Business Line Dropdown */}
                                            <label className="form-label mb-1 w-50 block text-xs mt-4">
                                                <span className="text-red-500">*</span> Select business line:
                                            </label>
                                            <select
                                                name="channel"
                                                value={form.channel}
                                                onChange={handleChange}
                                                disabled={isChannelDisabled}
                                                className="w-full border rounded px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-brand-primary"
                                            >
                                                {isPopup ? (
                                                    <>
                                                        {popupChannelOptions.length > 1 && <option value="">-- Select --</option>}
                                                        {popupChannelOptions.map((channelOption) => (
                                                            <option key={channelOption} value={channelOption}>{channelOption}</option>
                                                        ))}
                                                    </>
                                                ) : (
                                                    <>
                                                        <option value="">-- Select --</option>
                                                        {ALL_BUSINESS_LINES.map((businessLine) => (
                                                            <option key={businessLine} value={businessLine}>{businessLine}</option>
                                                        ))}
                                                    </>
                                                )}
                                            </select>
                                            {errors.channel && <p className="text-xs text-red-500">{errors.channel}</p>}

                                            <label className="form-label mb-1  w-50 block text-xs mt-4">
                                                Loan #:
                                            </label>
                                            <input
                                                name="loanNumber"
                                                value={form.loanNumber}
                                                onChange={handleChange}
                                                className="w-full border rounded px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-brand-primary"
                                            />
                                        </div>

                                        {/* Right column */}
                                        <div>
                                            <label className="form-label mb-1  w-50 block text-xs">
                                                <span className="text-red-500">*</span> Preferred E-mail:
                                            </label>
                                            <input
                                                name="email"
                                                type="email"
                                                value={form.email}
                                                onChange={handleChange}
                                                className="w-full border rounded px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-brand-primary"
                                            />
                                            {errors.email && <p className="text-xs text-red-500">{errors.email}</p>}


                                            <label className="form-label mb-1  w-50 block text-xs mt-4">
                                                Phone Number:
                                            </label>
                                            <input
                                                name="phoneNumber"
                                                value={form.phoneNumber}
                                                onChange={handleChange}
                                                className="w-full border rounded px-2 py-1 text-xs focus:outline-none focus:ring-2 focus:ring-brand-primary"
                                            />
                                            {errors.phoneNumber && <p className="text-xs text-red-500">{errors.phoneNumber}</p>}

                                        </div>
                                    </div>

                                    {/* Full-width message */}
                                    <div className="md:col-span-2">
                                        <label className="form-label mb-1  w-50 block text-xs">
                                            <span className="text-red-500">*</span> Message:
                                        </label>
                                        <textarea
                                            name="message"
                                            rows="3"
                                            value={form.message}
                                            onChange={handleChange}
                                            className="w-full border rounded px-2 py-1 text-xs resize-none focus:outline-none focus:ring-2 focus:ring-brand-primary"
                                        />
                                        {errors.message && <p className="text-xs text-red-500">{errors.message}</p>}
                                    </div>

                                    {/* Buttons */}
                                    <div className="flex justify-end mt-4">
                                        <button
                                            type="submit"
                                            disabled={submitting}
                                            className="bg-[#2080C3] text-white px-4 py-1 text-xs rounded-md hover:bg-[#5DA5D9] disabled:opacity-50 transition-colors"
                                        >
                                            {submitting ? "Submitting..." : "Submit"}
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default ContactForm;
